import {
  ArrowRightOutlined,
  BarChartOutlined,
  DollarOutlined,
  QuestionCircleOutlined,
  SyncOutlined,
  UsergroupAddOutlined,
} from '@ant-design/icons'
import Analytics from '@components/charts/analytics/Analytics'
import AverageSessionDurationPerUser from '@components/charts/user/AverageSessionDurationPerUser'
import DailyActiveUser from '@components/charts/user/DailyActiveUser'
import HasAccess from '@components/HasAccess'
import Page from '@components/Page/Page'
import QuickActions from '@components/QuickActions'
import { SayHello } from '@components/SayHello'
import { AnalyticsProvider } from '@contexts/AnalyticsContext'
import SearchDialog from '@modules/search-dialog'
import { PageTitle } from '@supplement-bacon/react-toolkit/components'
import { Permission } from '@utils/permissions'
import { Button, Card, Col, Flex, Row, Tooltip, Typography } from 'antd'
import dayjs from 'dayjs'
import { Link } from 'react-router-dom'

const { Text, Title } = Typography

const HomePage = () => {
  return (
    <Page noWrapWithCol>
      <Col span={24}>
        <Card styles={{ body: { padding: 16 } }}>
          <Flex vertical align="center" gap={40} style={{ padding: 25 }}>
            <Flex vertical gap={24}>
              <Title className="m-0">
                <SayHello />
              </Title>
              <SearchDialog.Button triggerDisplayMode="input" />
            </Flex>
            <QuickActions />
          </Flex>
        </Card>
      </Col>
      <Col span={24}>
        <AnalyticsProvider>
          <Flex style={{ display: 'none' }}>
            <Analytics.Date
              rangePresets={Analytics.rangesPresets.venue}
              defaultValue={[
                dayjs().subtract(7, 'day'),
                dayjs().subtract(1, 'day'),
              ]}
            />
          </Flex>
          <Flex vertical>
            <PageTitle level={5} title="Last 7 days" />
            <Row gutter={[16, 16]}>
              <Col span={8}>
                <Analytics.RowStatistics
                  entity="user"
                  kind="user-registration"
                />
              </Col>
              <Col span={8}>
                <Card styles={{ body: { padding: 16 } }}>
                  <Flex justify="space-between" align="center">
                    <Text type="secondary" className="m-0">
                      Daily active users{' '}
                      <Tooltip
                        title={
                          'Trend of unique users per day over the past 7 days.'
                        }
                      >
                        <QuestionCircleOutlined />
                      </Tooltip>
                    </Text>
                  </Flex>
                  <DailyActiveUser displayMode="compact" />
                </Card>
              </Col>

              <Col span={8}>
                <Card styles={{ body: { padding: 16 } }}>
                  <Flex justify="space-between" align="center">
                    <Text type="secondary" className="m-0">
                      Average session duration per user{' '}
                      <Tooltip
                        title={
                          'Average session duration per user & per day over the past 7 days.'
                        }
                      >
                        <QuestionCircleOutlined />
                      </Tooltip>
                    </Text>
                  </Flex>
                  <AverageSessionDurationPerUser displayMode="compact" />
                </Card>
              </Col>
            </Row>
          </Flex>
        </AnalyticsProvider>
      </Col>
      <Col span={24}>
        <Flex vertical>
          <PageTitle level={5} title="Explore your KPIs" />
          <Row gutter={[16, 16]}>
            <Col span={6}>
              <Card styles={{ body: { padding: 16 } }}>
                <Flex align="center" gap={10}>
                  <BarChartOutlined style={{ fontSize: 30 }} />
                  <Flex vertical gap={5}>
                    <Flex vertical gap={0}>
                      <Title level={5} className="m-0">
                        Traffic
                      </Title>
                      <Text type="secondary">View your traffic analytics</Text>
                    </Flex>
                    <Link to={'/overview/traffic'}>
                      <Button iconPlacement="end" icon={<ArrowRightOutlined />}>
                        Discover Traffic
                      </Button>
                    </Link>
                  </Flex>
                </Flex>
              </Card>
            </Col>

            <Col span={6}>
              <Card styles={{ body: { padding: 16 } }}>
                <Flex align="center" gap={10}>
                  <UsergroupAddOutlined style={{ fontSize: 30 }} />
                  <Flex vertical gap={5}>
                    <Flex vertical gap={0}>
                      <Title level={5} className="m-0">
                        Community
                      </Title>
                      <Text type="secondary">Understand your customers</Text>
                    </Flex>
                    <Link to={'/overview/community'}>
                      <Button iconPlacement="end" icon={<ArrowRightOutlined />}>
                        Discover Community
                      </Button>
                    </Link>
                  </Flex>
                </Flex>
              </Card>
            </Col>

            <Col span={6}>
              <Card styles={{ body: { padding: 16 } }}>
                <Flex align="center" gap={10}>
                  <SyncOutlined style={{ fontSize: 30 }} />
                  <Flex vertical gap={5}>
                    <Flex vertical gap={0}>
                      <Title level={5} className="m-0">
                        Conversion
                      </Title>
                      <Text type="secondary">Optimize your conversions</Text>
                    </Flex>
                    <Link to={'/overview/conversion'}>
                      <Button iconPlacement="end" icon={<ArrowRightOutlined />}>
                        Discover Conversion
                      </Button>
                    </Link>
                  </Flex>
                </Flex>
              </Card>
            </Col>
            <HasAccess permissions={Permission.createAdmin}>
              <Col span={6}>
                <Card styles={{ body: { padding: 16 } }}>
                  <Flex align="center" gap={10}>
                    <DollarOutlined style={{ fontSize: 30 }} />
                    <Flex vertical gap={5}>
                      <Flex vertical gap={0}>
                        <Title level={5} className="m-0">
                          Revenue
                        </Title>
                        <Text type="secondary">
                          Track your revenue analytics
                        </Text>
                      </Flex>
                      <Link to={'/overview/revenue'}>
                        <Button
                          iconPlacement="end"
                          icon={<ArrowRightOutlined />}
                        >
                          Discover Revenue
                        </Button>
                      </Link>
                    </Flex>
                  </Flex>
                </Card>
              </Col>
            </HasAccess>
          </Row>
        </Flex>
      </Col>
    </Page>
  )
}

export default HomePage
